import { useTranslation } from "react-i18next";
import i18n from "senf-shared/utils/i18n";
import { formatDateBasedOnLanguage, getPhaseOfDateRange } from "..";
import { Project } from "../types";

type ViewType = "overview" | "survey" | "ideaMap";

const getStartAndEndDates = (project, viewType) => {
  const startAndEndDates = [];

  project?.ideaMaps?.forEach(({ startDate, endDate, settings }) => {
    if (startDate && endDate && (viewType !== "overview" || settings?.publicAccess !== false)) {
      startAndEndDates.push({
        startDate,
        endDate,
        phase: getPhaseOfDateRange(startDate, endDate),
      });
    }
  });

  project?.surveys?.forEach(({ startDate, endDate, settings }) => {
    if (startDate && endDate && (viewType !== "overview" || settings?.publicAccess !== false)) {
      startAndEndDates.push({
        startDate,
        endDate,
        phase: getPhaseOfDateRange(startDate, endDate),
      });
    }
  });

  return startAndEndDates;
};

export const getStartAndEndDate = (project, viewType: ViewType = "overview") => {
  const startAndEndDates = getStartAndEndDates(project, viewType);

  let nextStartDate = null;
  let nextStartDateFormatted = null;
  let inactive = true;
  let phase = null;

  const hasValidDates = startAndEndDates.some(
    (item) => item.startDate && item.endDate
  );
  if (!hasValidDates) {
    phase = "notYetScheduled";
    return {
      phase,
      inactiveText: i18n?.t("global:participationNotYetScheduled"),
      inactive,
      lastEndDate: null,
      firstStartDate: null,
    };
  }

  const itemWithSmallestStartDate = startAndEndDates.reduce(
    (smallest, current) => {
      return new Date(current.startDate) < new Date(smallest.startDate)
        ? current
        : smallest;
    }
  );

  const itemWithLargestEndDate = startAndEndDates.reduce((largest, current) => {
    return new Date(current.endDate) > new Date(largest.endDate)
      ? current
      : largest;
  });

  const firstStartDate = new Date(itemWithSmallestStartDate.startDate);
  firstStartDate.setHours(0, 0, 0, 0);

  const firstStartDateFormatted = formatDateBasedOnLanguage(
    firstStartDate,
    i18n?.resolvedLanguage || "de"
  );
  const lastEndDate = new Date(itemWithLargestEndDate.endDate);
  lastEndDate.setHours(23, 59, 59, 999);
  const lastEndDateFormatted = formatDateBasedOnLanguage(
    lastEndDate,
    i18n?.resolvedLanguage || "de"
  );

  const validDates = startAndEndDates.filter((item) => {
    const start = new Date(item.startDate);
    const end = new Date(item.endDate);
    return (
      item.startDate &&
      item.endDate &&
      !isNaN(start.getTime()) &&
      !isNaN(end.getTime())
    );
  });

  const datesWithPhase = validDates.map((item) => {
    return {
      start: new Date(item.startDate),
      end: new Date(item.endDate),
      phase: getPhaseOfDateRange(item.startDate, item.endDate),
    };
  });

  const whilePhase = datesWithPhase.find((item) => item.phase === "while");

  if (whilePhase) {
    phase = "while";
    inactive = false;
  } else {
    const beforePhase = datesWithPhase
      .filter((item) => item.phase === "before" && item.start > new Date())
      .reduce((next, current) => {
        return !next || current.start < next.start ? current : next;
      }, null);

    if (beforePhase) {
      phase = "beforeNextParticipation";
      nextStartDate = beforePhase.start;
      nextStartDateFormatted = formatDateBasedOnLanguage(
        nextStartDate,
        i18n?.resolvedLanguage || "de"
      );
    }
  }

  if (!phase) {
    if (!phase && !lastEndDate && !firstStartDate) {
      phase = "notYetScheduled";
    } else if (new Date() > lastEndDate) {
      phase = "after";
    } else {
      // If we are here, it means there is no "while" or "beforeNextParticipation" phase and it's not "after", so it must be "before"
      phase = "before";
    }
    inactive = true; // 'before', 'after', and 'notYetScheduled' are considered inactive
  }

  const inactiveText = {
    before: `${i18n?.t(
      "global:participationNotOpenYet"
    )} ${firstStartDateFormatted}`,
    after: `${i18n?.t("global:participationClosed")} ${lastEndDateFormatted}`,
    notYetScheduled: i18n?.t("global:participationNotYetScheduled"),
    beforeNextParticipation: `${i18n?.t(
      "global:participationNextNotOpenYet"
    )} ${nextStartDateFormatted}`,
  }[phase];

  return {
    phase,
    inactiveText,
    inactive,
    lastEndDate,
    firstStartDate,
  };
};

export const useCheckParticipationPhase = () => {
  return (project: Project, viewType: ViewType = "overview") => {
    const { phase, inactiveText, inactive, lastEndDate, firstStartDate } =
      getStartAndEndDate(project, viewType);
    return {
      phase,
      inactiveText,
      inactive,
      lastEndDate,
      firstStartDate,
    };
  };
};

